import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../app.config.json";
import i18n from '@/i18n';
import store from '@/state/store';
import { isCommunityScopedPath } from '@/helpers/communityUrl';
import { applyLocale } from '@/helpers/locale';
import * as Sentry from '@sentry/vue';

const router = createRouter({
    history: createWebHistory(),
    routes,
    // Use the HTML5 history API (i.e. normal-looking routes)
    // instead of routes with hashes (e.g. example.com/#/about).
    // This may require some server configuration in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    mode: 'history',
    // Simulate native-like scroll behavior when navigating to a new
    // route and using back/forward buttons.
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0, left: 0 };
        }
    },
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
    console.log("router.beforeEach::Route to", routeTo);
    if (process.env.VUE_APP_DEFAULT_AUTH === "server") {
        // Check if auth is required on this route
        // (including nested routes).
        const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
        console.log("router.beforeEach::Auth required", authRequired);

        // If auth isn't required for the route, just continue.
        if (!authRequired) return next();
        
        // Special case: If we're coming from registration and going to onboarding,
        // allow it without validation to prevent token refresh issues
        if (routeFrom.name === 'register' && routeTo.name === 'onboarding') {
            console.log('Coming from registration to onboarding - skipping validation');
            return next();
        }
        
        // If auth is required and the user is logged in...
        if (store.getters['auth/loggedIn']) {
            // Validate the local user token...
            try {
                const validatePromise = store.dispatch('auth/validate');
                
                // Check if the dispatch returned a valid promise
                if (validatePromise && typeof validatePromise.then === 'function') {
                    return validatePromise.then((validUser) => {
                        // Then continue if the token still represents a valid user,
                        // otherwise redirect to login.
                        if (validUser) {
                            // Helper placed at top so it is defined before first use
                            const handleSubscriptionRedirect = async () => {
                                // Quickly identify subscription-related routes that we should allow
                                const subscriptionRouteNames = [
                                    'subscription-overview',
                                    'subscription-plans',
                                    'subscription-manage',
                                    'subscription-success',
                                    'subscription-cancel',
                                    'payment-success',
                                    'onboarding' // Add onboarding to prevent loop
                                ];

                                // If we're already on a subscription route – continue
                                if (subscriptionRouteNames.includes(routeTo.name)) {
                                    next();
                                    return;
                                }

                                try {
                                    // Fetch subscription only if we don't have it loaded yet
                                    if (store.state.subscription.currentSubscription === null && !store.state.subscription.loading) {
                                        await store.dispatch('subscription/fetchCurrentSubscription');
                                    }
                                } catch (error) {
                                    // Handle 404 errors gracefully - likely means no subscription endpoint or service
                                    console.warn('Failed to fetch subscription:', error);
                                    // Continue without redirection if subscription service is unavailable
                                    next();
                                    return;
                                }

                                const hasActiveSub = store.getters['subscription/isSubscribed'];

                                if (!hasActiveSub) {
                                    // Redirect to plan selection
                                    next({ name: 'subscription-plans' });
                                    return;
                                }

                                // User has active subscription – proceed as normal
                                next();
                            };

                            // ------------------------------------------------------------------
                            // 1) Onboarding check
                            // ------------------------------------------------------------------
                            if (!store.getters['auth/hasCompletedOnboarding'] && routeTo.name !== 'onboarding') {
                                console.log("onboarding not completed")
                                // Ensure we have the latest profile before deciding
                                if (!store.state.auth.currentUser) {
                                    console.log("fetching profile")
                                    return store.dispatch('auth/fetchProfile')
                                        .then(profile => {
                                            if (profile && profile.completed_onboarding) {
                                                // continue to subscription validation below
                                                return handleSubscriptionRedirect();
                                            } else {
                                                return next({ name: 'onboarding' });
                                            }
                                        })
                                        .catch(() => {
                                            // On error, continue without redirection
                                            return next();
                                        });
                                }
                                // If onboarding not completed – redirect there first
                                return next({ name: 'onboarding' });
                            } else {
                                // ------------------------------------------------------------------
                                // 2) Subscription validation
                                // ------------------------------------------------------------------
                                return handleSubscriptionRedirect();
                            }
                        } else {
                            redirectToLogin();
                        }
                    }).catch((error) => {
                        console.error('Auth validation failed:', error);
                        redirectToLogin();
                    });
                } else {
                    // If validate doesn't return a promise, just continue
                    console.warn('Auth validate did not return a promise');
                    next();
                }
            } catch (error) {
                console.error('Error calling auth validate:', error);
                redirectToLogin();
            }
        } else {
            // If auth is required and the user is NOT currently logged in,
            // redirect to login.
            redirectToLogin();
        }

        // eslint-disable-next-line no-unused-vars
        // eslint-disable-next-line no-inner-declarations
        function redirectToLogin() {
            // Pass the original route to the login component
            next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
        }
    } else {
        const publicPages = ['/login', '/register', '/forgot-password', '/']; // Add root path as public
        const authpage = !publicPages.includes(routeTo.path);
        const loggeduser = localStorage.getItem('user');

        if (authpage && !loggeduser) {
            return next('/login');
        }

        next();
    }
});

// ---------------------------------------------------------------------------
// Community URL pinning (?c=<id>).
// Each tab carries its active community in the URL so several tabs can work on
// different communities in parallel:
//  - Community-scoped routes without ?c get it injected from the store (the
//    tab's current community), keeping the address bar shareable/deterministic.
//  - Navigating to a URL that carries a different ?c adopts that community
//    BEFORE the target page mounts, so all mount-time fetches use it.
// Runs after the auth guard above (guards execute in registration order).
// ---------------------------------------------------------------------------
router.beforeEach((to, from) => {
    if (!isCommunityScopedPath(to.path)) return true;

    const currentId = store.state.community.currentCommunityId;
    const rawC = to.query.c;

    // Garbage / non-numeric ?c: strip it and fall back to the tab's community.
    if (rawC !== undefined && !/^\d+$/.test(String(rawC))) {
        const query = { ...to.query };
        delete query.c;
        return { path: to.path, query, hash: to.hash, replace: true };
    }

    const urlId = rawC !== undefined ? Number(rawC) : null;

    if (urlId === null) {
        // No community in the store yet (first boot before getCommunities /
        // onboarding): let the navigation through, later ones get the param.
        if (currentId === null) return true;
        return {
            path: to.path,
            query: { ...to.query, c: String(currentId) },
            hash: to.hash,
            replace: true,
        };
    }

    if (urlId !== currentId) {
        // Only adopt a ?c we can vouch for: once the communities list is
        // loaded, an unknown id (deleted community / someone else's link)
        // is stripped and falls back to the tab's own community.
        const communities = store.state.community.communities;
        const isKnown = communities.length === 0 ||
            communities.some((community) => community.id === urlId);
        if (!isKnown) {
            const query = { ...to.query };
            delete query.c;
            return { path: to.path, query, hash: to.hash, replace: true };
        }
        // Explicit community in the target URL wins: adopt it before the page
        // mounts so every mount-time fetch uses the right community.
        store.commit('community/SET_CURRENT_COMMUNITY', urlId);
        if (from.name && to.name === from.name) {
            // Same component, only ?c changed — it would not remount, leaving
            // mounted-only pages on stale data. Force a full document load.
            window.location.assign(to.fullPath);
            return false;
        }
    }
    return true;
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
    // Create a `beforeResolve` hook, which fires whenever
    // `beforeRouteEnter` and `beforeRouteUpdate` would. This
    // allows us to ensure data is fetched even when params change,
    // but the resolved route does not. We put it in `meta` to
    // indicate that it's a hook we created, rather than part of
    // Vue Router (yet?).
    try {
        // For each matched route...
        for (const route of routeTo.matched) {
            await new Promise((resolve, reject) => {
                // If a `beforeResolve` hook is defined, call it with
                // the same arguments as the `beforeEnter` hook.
                if (route.meta && route.meta.beforeResolve) {
                    route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
                        // If the user chose to redirect...
                        if (args.length) {
                            // If redirecting to the same route we're coming from...
                            // Complete the redirect.
                            next(...args);
                            reject(new Error('Redirected'));
                        } else {
                            resolve();
                        }
                    });
                } else {
                    // Otherwise, continue resolving the route.
                    resolve();
                }
            });
        }
        // If a `beforeResolve` hook chose to redirect, just return.
    } catch (error) {
        return;
    }
    // A public page's language is part of its URL (`/about` vs `/en/about`), so
    // the route - not localStorage - decides what the visitor reads. Apply it
    // before the title is resolved below, or the page and its <title> disagree.
    if (routeTo.meta.locale) {
        applyLocale(routeTo.meta.locale);
    }
    // Public marketing pages carry their own full <title> (meta.titleKey, translated
    // and shown standalone). App pages keep the "<page> | Affiloop" pattern.
    const pageTitle = routeTo.meta.titleKey
        ? i18n.global.t(routeTo.meta.titleKey)
        : routeTo.meta.title;
    if (pageTitle) {
        document.title = routeTo.meta.standaloneTitle
            ? pageTitle
            : pageTitle + ' | ' + appConfig.title;
    }
    // If we reach this point, continue resolving the route.
    next();
});

router.afterEach((to, from) => {
    if(process.env.NODE_ENV === 'production') {
        if (window.gtag) {
            gtag('config', process.env.VUE_APP_GOOGLE_ANALYTICS_ID, {
                page_path: to.fullPath,
                page_title: to.name || document.title
            });
        }
    }
});

// ---------------------------------------------------------------------------
// Navigation errors.
//
// Every route component is loaded lazily, so a navigation fetches its bundle
// from the server at the moment of the click. When that fetch fails — a weak
// mobile link, a bundle chunk the browser cannot parse, a stale build — the
// navigation rejects, nothing is mounted, and the user is left silently on the
// page they were already on. Recovering with a full document load re-resolves
// the bundle from scratch and takes them where they asked to go.
// ---------------------------------------------------------------------------
const CHUNK_RELOAD_KEY = 'lastChunkRecoveryReload';
const CHUNK_RELOAD_COOLDOWN_MS = 30000;

function isBundleLoadError(error) {
    const message = String((error && (error.message || error.name)) || '');
    return /Loading( CSS)? chunk .* failed/i.test(message)
        || /error loading dynamically imported module/i.test(message)
        || /Importing a module script failed/i.test(message)
        || /ChunkLoadError/i.test(message);
}

router.onError((error, to, from) => {
    console.error('Router navigation error:', error);
    // Report it: a navigation that dies here is invisible to the user, so the
    // only way we ever learn about it is the error tracker.
    Sentry.captureException(error, {
        extra: {
            to: to && to.fullPath,
            from: from && from.fullPath,
            bundleLoadError: isBundleLoadError(error),
        },
    });

    if (!isBundleLoadError(error) || !to || !to.fullPath) return;

    // A full reload cannot fix a bundle that is genuinely broken, so never
    // reload the same target twice in a row — that would be a reload loop.
    let previous = null;
    try {
        previous = JSON.parse(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 'null');
    } catch (e) {
        previous = null;
    }
    if (previous && previous.path === to.fullPath && Date.now() - previous.at < CHUNK_RELOAD_COOLDOWN_MS) return;

    try {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ path: to.fullPath, at: Date.now() }));
    } catch (e) {
        // Private mode / storage disabled: recovering once is still better than
        // leaving the user stuck, so fall through to the reload.
    }

    window.location.assign(to.fullPath);
});

export default router;